import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Badge } from './badge';
import { Button } from './button';
import { Progress } from './progress';
import { apiClient } from '../../lib/api';
import { useTenantContext } from '../../contexts/TenantContext';

interface SmsCreditsData {
  tenant_id: string;
  plan: string;
  is_trial: boolean;
  subscription_status: string;
  billing_cycle: string;
  current_period: {
    start: number;
    end: number;
    type: string;
    days_remaining: number;
  };
  stripe_subscription_id?: string;
  stripe_usage_item_id?: string;
  credits_summary: {
    included_credits: {
      limit: number;
      used: number;
      remaining: number;
      resets_at: number;
      days_until_reset: number;
    };
    overage: {
      used: number;
      rate_per_sms: number;
      estimated_cost: number;
    };
    purchased_credits: {
      total: number;
      bundles: number;
      note: string;
    };
    total_used_this_period: number;
    usage_percentage: number;
    billing_model: string;
  };
  purchased_credits: Array<{
    purchase_id: string;
    credits: number;
    remaining: number;
    purchased_at: number;
    price: number;
  }>;
  billing_model: string;
}

interface SmsCreditsCardProps {
  showDetails?: boolean;
  className?: string;
}

export default function SmsCreditsCard({ showDetails = false, className = "" }: SmsCreditsCardProps) {
  const { selectedTenantId } = useTenantContext();
  const [creditsData, setCreditsData] = useState<SmsCreditsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedTenantId) {
      loadCreditsData();
    }
  }, [selectedTenantId]);

  const loadCreditsData = async () => {
    if (!selectedTenantId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getSmsCredits(selectedTenantId);
      setCreditsData(data);
    } catch (err) {
      console.error('Failed to load SMS credits:', err);
      setError('Failed to load SMS credits');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-orange-600';
    return 'text-green-600';
  };



  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !creditsData) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <p className="text-red-600">{error || 'No data available'}</p>
          <Button variant="outline" size="sm" onClick={loadCreditsData} className="mt-2">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Check if credits_summary exists before destructuring
  if (!creditsData.credits_summary) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <p className="text-gray-600">SMS credits data not available for this tenant</p>
        </CardContent>
      </Card>
    );
  }

  const { credits_summary, current_period, purchased_credits } = creditsData;
  const { included_credits: plan_credits, purchased_credits: purchased_summary, usage_percentage } = credits_summary;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">SMS Credits</CardTitle>
          {creditsData.is_trial && (
            <Badge variant="secondary">Trial</Badge>
          )}
        </div>
        <CardDescription>
          Included: 1,000 SMS per cycle; Overage: $0.03 each (auto-billed by Stripe)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Credits Display */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold">
              {plan_credits.used.toLocaleString()}
            </span>
            <span className="text-sm text-gray-600">
              SMS used this cycle
            </span>
          </div>
          
          {/* Usage Progress */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span>Included SMS</span>
              <span className={getUsageColor(usage_percentage)}>
                {Math.min(plan_credits.used, plan_credits.limit).toLocaleString()} / {plan_credits.limit.toLocaleString()} ({Math.min(usage_percentage, 100)}%)
              </span>
            </div>
            <Progress 
              value={Math.min(usage_percentage, 100)} 
              className="h-2"
            />
            
            {plan_credits.used > plan_credits.limit && (
              <div className="flex items-center justify-between text-sm text-orange-600">
                <span>Overage SMS</span>
                <span>
                  {(plan_credits.used - plan_credits.limit).toLocaleString()} × $0.03 = ${((plan_credits.used - plan_credits.limit) * 0.03).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Credits Breakdown */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="text-center">
            <div className="text-lg font-semibold">
              {Math.max(0, plan_credits.limit - plan_credits.used).toLocaleString()}
            </div>
            <div className="text-xs text-gray-600">Included Remaining</div>
            <div className="text-xs text-gray-500">
              Resets {formatDate(plan_credits.resets_at)}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-orange-600">
              {Math.max(0, plan_credits.used - plan_credits.limit).toLocaleString()}
            </div>
            <div className="text-xs text-gray-600">Overage SMS</div>
            <div className="text-xs text-gray-500">
              Billed by Stripe
            </div>
          </div>
        </div>
        
        {/* Optional Purchased Credits */}
        {purchased_summary.total > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 text-sm font-medium">
                Purchased Credits (Optional)
              </span>
              <span className="text-blue-600 font-semibold">
                {purchased_summary.total.toLocaleString()}
              </span>
            </div>
            <p className="text-blue-600 text-xs mt-1">
              {purchased_summary.bundles} bundle{purchased_summary.bundles !== 1 ? 's' : ''} • Non-billing credits
            </p>
          </div>
        )}

        {/* Detailed View */}
        {showDetails && (
          <div className="space-y-3 pt-3 border-t">
            <h4 className="font-medium text-sm">Billing Period Details</h4>
            <div className="text-xs space-y-1 text-gray-600">
              <div className="flex justify-between">
                <span>Period:</span>
                <span>{formatDate(current_period.start)} - {formatDate(current_period.end)}</span>
              </div>
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="capitalize">{current_period.type}</span>
              </div>
              <div className="flex justify-between">
                <span>Days Remaining:</span>
                <span>{current_period.days_remaining}</span>
              </div>
            </div>

            {purchased_credits.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Purchased Credits</h4>
                <div className="space-y-1">
                  {purchased_credits.map((credit) => (
                    <div key={credit.purchase_id} className="flex justify-between text-xs">
                      <span>{formatDate(credit.purchased_at)}</span>
                      <span>{credit.remaining.toLocaleString()} / {credit.credits.toLocaleString()}</span>
                      <span className="text-gray-500">${credit.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info States */}
        {plan_credits.used > plan_credits.limit && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <span className="text-orange-600 text-sm">💳</span>
              <span className="text-orange-700 text-sm font-medium">
                Overage charges apply
              </span>
            </div>
            <p className="text-orange-600 text-xs mt-1">
              {(plan_credits.used - plan_credits.limit).toLocaleString()} SMS × $0.03 = ${((plan_credits.used - plan_credits.limit) * 0.03).toFixed(2)} will be added to your next invoice.
            </p>
          </div>
        )}

        {usage_percentage >= 75 && plan_credits.used <= plan_credits.limit && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 text-sm">📊</span>
              <span className="text-blue-700 text-sm font-medium">
                {usage_percentage}% of included SMS used
              </span>
            </div>
            <p className="text-blue-600 text-xs mt-1">
              Additional SMS will be charged at $0.03 each. Resets in {plan_credits.days_until_reset} days.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}